const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/redux4Requests.ts';
import {
    ActionHandler,
    IActionHandlersMap,
    IKISAPIAction,
    IKisApiActionsMap,
    KisApiAction,
    makeReducer,
    ReducerState,
} from '../defs/actionTypes';
import Imm from 'immutable';
import { KeyValuePair, KISIM_TIME_FORMAT } from '../defs/generalDefs';
import moment from 'moment';
import { RemoteDataExtras, RemoteDataOptions, useRemoteData } from '../hooks/useRemoteData';
import { useSelector } from 'react-redux';
import { Dispatch } from 'redux';

// CONSTANTS

// Just empty map; reducer is expected to set values, selectors are expected to deal with defaults
export const defaultInitialState = Imm.fromJS({});

const KEY_DATA = 'data';
const KEY_REQUEST_STATUS = 'requestStatus';
const KEY_LAST_UPDATE = 'lastUpdate';
const KEY_META = 'meta';
const KEY_ERROR = 'error';

// defs

export enum RequestStatus {
    PENDING = 'PENDING',
    LOADING = 'LOADING',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
}

export type StoreEntry = {
    [KEY_DATA]: any;
    [KEY_REQUEST_STATUS]: RequestStatus | undefined;
    [KEY_LAST_UPDATE]: string | undefined;
    [KEY_META]: KeyValuePair | undefined;
    // The failure response payload, retained on failed requests so consumers can surface why it failed (see getError).
    [KEY_ERROR]?: unknown;
};

type StoredMap = { [key: string]: StoreEntry };

type StoreKeys = string[];

type KeysGeneratorFunction = (action: KisApiAction) => StoreKeys;

type DataProcessorFunction = (action: KisApiAction, state?: ReducerState) => any;

type UseRemoteData2Params<StoreState, DataType> = {
    canRequest?: BooleanOrFunction<StoreState>; // function or boolean to determine whether the request can be made (preconditions for fetch have been met)
    keys?: StoreKeys; // keys/path under which the data needs to be stored
    request: (dispatch: Dispatch<KisApiAction>, state: StoreState) => void;
    requestAgainAfter?: number; // in minutes
    requestOnMount?: boolean; // when set to true, the request is made when a component is mounted (and once all requirements are met), even if the data has been loaded before
    select: (state: StoreState) => DataType; // selector function to return the data; please define a proper return type for the selector!!
    shouldRequest?: BooleanOrFunction<StoreState>; // function or boolean to determine whether data needs to be requested; this is on top of the default check that checks whether data has been loaded or is being loaded
};

// Internal utility functions

const getMap = (state: ReducerState, keys: StoreKeys = []) => {
    const immutableStoredMap = state.getIn(keys);
    if (immutableStoredMap) {
        return immutableStoredMap.toJS() as StoredMap;
    }
    return undefined;
};

// Utility functions for reducers

export const getDataFromPayloadWithListObject = (action: KisApiAction) => Object.values(action.payload.List || {});

export const storeData = (state: ReducerState, data: any, keys: StoreKeys = []) => {
    state = state.setIn([...keys, KEY_DATA], data);
    return state;
};

export const removeStructure = (state: ReducerState, keys: StoreKeys = []) => {
    if (keys.length > 0) {
        state = state.deleteIn(keys);
    }
    return state;
};

// dataKeys can be used when the store contains the result of several requests with a specific identifier/key
export const defaultKisApiActionPending = (state: ReducerState, keys: StoreKeys = [], action: KisApiAction) => {
    state = state.setIn([...keys, KEY_REQUEST_STATUS], RequestStatus.LOADING);
    state = state.setIn([...keys, KEY_META], action.meta);
    return state;
};

export const defaultKisApiActionSuccess = (state: ReducerState, data: any, keys: StoreKeys = []) => {
    state = storeData(state, data, keys);
    state = state.setIn([...keys, KEY_REQUEST_STATUS], RequestStatus.SUCCESS);
    state = state.setIn([...keys, KEY_LAST_UPDATE], moment().format(KISIM_TIME_FORMAT));
    state = state.setIn([...keys, KEY_ERROR], undefined);
    return state;
};

export const defaultKisApiActionFailure = (state: ReducerState, keys: StoreKeys = [], action: KisApiAction) => {
    state = state.setIn([...keys, KEY_REQUEST_STATUS], RequestStatus.FAILED);
    state = state.setIn([...keys, KEY_LAST_UPDATE], undefined);
    state = state.setIn([...keys, KEY_ERROR], action.payload);
    return state;
};

const resolveKeysForRequestActionHandlers = (
    keysOrKeysGenerator: StoreKeys | KeysGeneratorFunction,
    action: KisApiAction
) => {
    return typeof keysOrKeysGenerator === 'function' ? keysOrKeysGenerator(action) : keysOrKeysGenerator;
};

// Adds a simple default implementation of action handlers; will store loading/success state, and
// by default stores the payload (this can be specified to behave differently if so desired). If
// something more special needs to be done, the whole success handler can be redefined
export const addRequestActionHandlers = (
    handlers: IActionHandlersMap,
    actionMap: IKisApiActionsMap,
    keysOrKeyGenerator: StoreKeys | KeysGeneratorFunction = [],
    dataProcessor?: DataProcessorFunction,
    successHandler?: ActionHandler<KisApiAction>,
    pendingHandler?: ActionHandler<KisApiAction>,
    failureHandler?: ActionHandler<KisApiAction>
) => {
    // PENDING
    handlers[actionMap.pending] = pendingHandler
        ? pendingHandler
        : (state: ReducerState, action: KisApiAction) => {
              const resolvedKeys = resolveKeysForRequestActionHandlers(keysOrKeyGenerator, action);
              return defaultKisApiActionPending(state, resolvedKeys, action);
          };

    // SUCCESS
    handlers[actionMap.success] = successHandler
        ? successHandler
        : (state: ReducerState, action: KisApiAction) => {
              const resolvedKeys = resolveKeysForRequestActionHandlers(keysOrKeyGenerator, action);
              const data = dataProcessor ? dataProcessor(action, state) : action.payload;
              return defaultKisApiActionSuccess(state, data, resolvedKeys);
          };

    // FAILURE
    handlers[actionMap.failure] = failureHandler
        ? failureHandler
        : (state: ReducerState, action: KisApiAction) => {
              const resolvedKeys = resolveKeysForRequestActionHandlers(keysOrKeyGenerator, action);
              return defaultKisApiActionFailure(state, resolvedKeys, action);
          };
};

// Utility functions for selectors

export const getRequestStatus = (state: ReducerState, keys: StoreKeys = []): RequestStatus => {
    return state.getIn([...keys, KEY_REQUEST_STATUS], RequestStatus.PENDING);
};

export const getLastUpdateMoment = (state: ReducerState, keys: StoreKeys = []) => {
    const lastUpdated = state.getIn([...keys, KEY_LAST_UPDATE], undefined);
    return lastUpdated ? moment(lastUpdated, KISIM_TIME_FORMAT) : undefined;
};

export const getRequestStatusMapFromImmMap = (immMap: Imm.Map<string, any>) => {
    const map = immMap.toJS() as KeyValuePair<StoreEntry>;

    return Object.keys(map).reduce((accumulator: KeyValuePair<RequestStatus>, key) => {
        accumulator[key] = map[key].requestStatus || RequestStatus.PENDING;
        return accumulator;
    }, {});
};

export const getRequestStatusMap = (
    state: ReducerState,
    keys: StoreKeys = []
): KeyValuePair<RequestStatus> | undefined => {
    const immMap = state.getIn(keys);
    if (immMap) {
        return getRequestStatusMapFromImmMap(immMap);
    }
    return undefined;
};

export const getOverallStatusFromMap = (statusMap: KeyValuePair<RequestStatus> | undefined) => {
    if (!statusMap) return RequestStatus.PENDING;

    if (Object.values(statusMap).every(requestStatus => requestStatus === RequestStatus.SUCCESS)) {
        return RequestStatus.SUCCESS;
    } else if (Object.values(statusMap).some(requestStatus => requestStatus === RequestStatus.LOADING)) {
        return RequestStatus.LOADING;
    } else if (Object.values(statusMap).some(requestStatus => requestStatus === RequestStatus.FAILED)) {
        return RequestStatus.FAILED;
    }

    return RequestStatus.PENDING;
};

export const getIsPendingOrLoading = (state: ReducerState, keys: StoreKeys = []) => {
    const status = getRequestStatus(state, keys);
    return status === RequestStatus.PENDING || status === RequestStatus.LOADING;
};

export const isAnyPendingOrLoading = (state: ReducerState, keys: StoreKeys = []) => {
    const statusMap = getRequestStatusMap(state, keys);
    const status = getOverallStatusFromMap(statusMap);
    return status === RequestStatus.PENDING || status === RequestStatus.LOADING;
};

export const isLoading = (state: ReducerState, keys: StoreKeys = []): boolean => {
    return state.getIn([...keys, KEY_REQUEST_STATUS]) === RequestStatus.LOADING;
};

export const getData = (state: ReducerState, keys: StoreKeys = []) => {
    return state.getIn([...keys, KEY_DATA]);
};

/** The retained failure payload of a request (see {@link defaultKisApiActionFailure}); undefined unless it failed. */
export const getError = (state: ReducerState, keys: StoreKeys = []) => {
    return state.getIn([...keys, KEY_ERROR]);
};

// converts data of individual items in store into a map
export const getDataAsMap = (state: ReducerState, keys: StoreKeys = []) => {
    const storedMap = getMap(state, keys);
    if (storedMap) {
        const map: KeyValuePair = {};
        Object.keys(storedMap).forEach(key => {
            if (storedMap[key][KEY_REQUEST_STATUS] === RequestStatus.SUCCESS) {
                map[key] = storedMap[key][KEY_DATA];
            }
        });
        return map;
    }
    return undefined;
};

// converts data of individual items in store into an array
export const getDataAsArray = (state: ReducerState, keys: StoreKeys = []) => {
    const map = getDataAsMap(state, keys);
    if (map) {
        return Object.values(map);
    }
    return undefined;
};

// Utility functions to help with useRemoteData hooks

type BooleanOrFunction<StoreState> = ((state: StoreState) => boolean) | boolean;

const getBooleanOrFunctionResult = <StoreState>(
    booleanOrFunction: BooleanOrFunction<StoreState> = true,
    state: StoreState
) => {
    if (typeof booleanOrFunction === 'function') {
        return booleanOrFunction(state);
    }
    return booleanOrFunction;
};

// Different implementation of useRemoteData that is catered to this setup for the store

const createRemoteDataOptions = <StoreState>(
    storeKey: keyof StoreState
): RemoteDataOptions<UseRemoteData2Params<StoreState, any>, any> => {
    return {
        isLoading: (state: StoreState, params) => {
            // For useRemoteData, pending and loading are considered "in the process of loading". This is so
            // on an initial render, a loading indicator can already be shown, which makes sense, because
            // data is going to be loaded.
            return getIsPendingOrLoading(state[storeKey] as ReducerState, params.keys);
        },
        select: (state: StoreState, params) => params.select(state),
        shouldFetch: (state: StoreState, params, extras: RemoteDataExtras<any>) => {
            const { canRequest, requestAgainAfter, keys, requestOnMount, shouldRequest } = params;
            const canFetchResult = getBooleanOrFunctionResult(canRequest, state);
            if (!canFetchResult) return false;

            const shouldFetchResult = getBooleanOrFunctionResult(shouldRequest, state);
            if (!shouldFetchResult) return false;

            const reducerState = state[storeKey] as ReducerState;

            const requestStatus = getRequestStatus(reducerState, keys);
            if (requestStatus === RequestStatus.LOADING) return false;

            const getIsTimeoutReached = () => {
                const lastUpdatedMoment = getLastUpdateMoment(reducerState, keys);
                if (requestAgainAfter && lastUpdatedMoment) {
                    return moment().isAfter(lastUpdatedMoment.add(requestAgainAfter, 'minutes'));
                }
                return false;
            };

            return (
                requestStatus === RequestStatus.PENDING ||
                (!!requestOnMount && !extras.didFetch) ||
                getIsTimeoutReached()
            );
        },
        fetch: (dispatch, state: StoreState, params) => {
            params.request(dispatch, state);
        },
    };
};

export type UseRemoteData2Result<DataType = unknown> = {
    data: DataType;
    isPendingOrLoading: boolean;
    status: RequestStatus;
    request: () => void;
};

export const useRemoteData2 = <StoreState, DataType>(
    reducerKey: keyof StoreState,
    params: UseRemoteData2Params<StoreState, DataType>
): UseRemoteData2Result<DataType> => {
    const requestStatus = useSelector((state: StoreState) =>
        getRequestStatus(state[reducerKey] as ReducerState, params.keys)
    );
    const isPendingOrLoading = useSelector((state: StoreState) =>
        getIsPendingOrLoading(state[reducerKey] as ReducerState, params.keys)
    );

    const [data, _, request] = useRemoteData({ ...params }, () => createRemoteDataOptions(reducerKey));
    return {
        data,
        isPendingOrLoading,
        status: requestStatus,
        request,
    };
};

export const useSaveDataRemotely = <StoreState, SaveFunction>(
    reducerKey: keyof StoreState,
    save: SaveFunction,
    keys?: StoreKeys
): [save: SaveFunction, requestStatus: RequestStatus] => {
    const requestStatus = useSelector((state: StoreState) => getRequestStatus(state[reducerKey] as ReducerState, keys));

    return [save, requestStatus];
};

// Setup to more simply create a reducer

export type KisApiActionHandlersParams = [
    kisApiActionMap: IKisApiActionsMap,
    keys?: string[] | KeysGeneratorFunction | undefined,
    dataProcessor?: DataProcessorFunction | undefined,
    successHandler?: ActionHandler<IKISAPIAction> | undefined,
    pendingHandler?: ActionHandler<IKISAPIAction> | undefined,
    failureHandler?: ActionHandler<IKISAPIAction> | undefined,
];

export const makeReducer2 = (
    params: KisApiActionHandlersParams[],
    additionalHandlers: {
        [actionType: string]: ActionHandler;
    } = {}
) => {
    const handlers: IActionHandlersMap = {};

    params.forEach(actionHandlerParams => {
        const keys = actionHandlerParams[1];
        if (keys) {
            if (!(Array.isArray(keys) || typeof keys === 'function')) {
                console.error('Incorrect argument value for keys: ', keys);
            }
        }

        addRequestActionHandlers(
            handlers,
            actionHandlerParams[0],
            actionHandlerParams[1],
            actionHandlerParams[2],
            actionHandlerParams[3],
            actionHandlerParams[4],
            actionHandlerParams[5]
        );
    });

    Object.keys(additionalHandlers).forEach(key => {
        handlers[key] = additionalHandlers[key];
    });

    return makeReducer(handlers);
};
